<template>
    <div>
        <div class="text-center mb-2">
            <h1 class="text-theme-primary text-3xl font-bold">
                Registrar venta
            </h1>
            <p class="mt-2 text-theme-base max-w-2xl mx-auto text-sm">
                Elige la herramienta que mejor se adapte al tipo de venta que
                necesitas registrar.
            </p>
            <wiki-learn-more-link topic="orden-vs-venta" class="mt-2" />
        </div>

        <div class="container mx-auto mt-8">
            <div class="flex flex-wrap justify-center gap-6">
                <router-link
                    to="/app/orders/new"
                    class="text-decoration-none w-full sm:w-80"
                >
                    <div
                        class="button-card h-full bg-brand-magenta rounded-xl p-5 shadow-sm text-white flex flex-col"
                    >
                        <div class="flex items-start gap-3 mb-3">
                            <span class="text-4xl leading-none">🌐</span>
                            <div>
                                <span
                                    class="inline-block text-xs font-semibold bg-white bg-opacity-20 text-white px-2 py-0.5 rounded-full mb-1"
                                >
                                    {{
                                        hasMerchantScope
                                            ? "Precios globales"
                                            : "Precios de catálogo"
                                    }}
                                </span>
                                <h5
                                    class="text-white font-semibold leading-tight"
                                >
                                    {{
                                        hasMerchantScope
                                            ? "Orden ecommerce"
                                            : "Registrar venta"
                                    }}
                                </h5>
                            </div>
                        </div>

                        <p class="text-sm mb-3 opacity-90">
                            {{
                                hasMerchantScope
                                    ? "Registra pedidos usando los precios del catálogo global de la plataforma, ideal para ventas en línea."
                                    : "Registra pedidos usando los precios de tu catálogo."
                            }}
                        </p>

                        <hr class="mb-3 opacity-20" />

                        <ul
                            class="text-sm space-y-2 flex-1 list-none ps-0 opacity-90"
                        >
                            <li class="flex gap-2">
                                <span class="flex-shrink-0">✅</span>
                                <span
                                    >Busca entre todos los productos del
                                    catálogo</span
                                >
                            </li>
                            <li class="flex gap-2">
                                <span class="flex-shrink-0">✅</span>
                                <span>{{
                                    hasMerchantScope
                                        ? "Usa precios predefinidos del ecommerce"
                                        : "Usa los precios de tu catálogo"
                                }}</span>
                            </li>
                            <li class="flex gap-2">
                                <span class="flex-shrink-0">✅</span>
                                <span
                                    >Soporta cotizaciones, abonos y fecha de
                                    entrega</span
                                >
                            </li>
                            <li v-if="hasMerchantScope" class="flex gap-2">
                                <span class="flex-shrink-0">⚠️</span>
                                <span
                                    >Puede incluir productos sin inventario en
                                    la tienda</span
                                >
                            </li>
                        </ul>

                        <div
                            class="mt-4 text-center text-sm font-semibold text-white"
                        >
                            Ir a la herramienta →
                        </div>
                    </div>
                </router-link>

                <router-link
                    v-if="hasMerchantScope"
                    to="/app/orders/new-in-merchant"
                    class="text-decoration-none w-full sm:w-80"
                >
                    <div
                        class="button-card h-full bg-brand-blue rounded-xl p-5 shadow-sm text-white flex flex-col"
                    >
                        <div class="flex items-start gap-3 mb-3">
                            <span class="text-4xl leading-none">🛍</span>
                            <div>
                                <span
                                    class="inline-block text-xs font-semibold bg-white bg-opacity-20 text-white px-2 py-0.5 rounded-full mb-1"
                                >
                                    Con inventario
                                </span>
                                <h5
                                    class="text-white font-semibold leading-tight"
                                >
                                    Venta en tienda
                                </h5>
                            </div>
                        </div>

                        <p class="text-sm mb-3 opacity-90">
                            Registra una venta usando los productos y precios
                            configurados en tu tienda, con descuento de
                            inventario.
                        </p>

                        <hr class="mb-3 opacity-20" />

                        <ul
                            class="text-sm space-y-2 flex-1 list-none ps-0 opacity-90"
                        >
                            <li class="flex gap-2">
                                <span class="flex-shrink-0">✅</span>
                                <span
                                    >Busca productos por nombre o código de
                                    barras</span
                                >
                            </li>
                            <li class="flex gap-2">
                                <span class="flex-shrink-0">✅</span>
                                <span
                                    >Usa los precios propios de tu tienda</span
                                >
                            </li>
                            <li class="flex gap-2">
                                <span class="flex-shrink-0">✅</span>
                                <span
                                    >Descuenta inventario al registrar la
                                    venta</span
                                >
                            </li>
                            <li class="flex gap-2">
                                <span class="flex-shrink-0">✅</span>
                                <span
                                    >Soporta descuentos, abonos y fecha de
                                    entrega</span
                                >
                            </li>
                        </ul>

                        <div
                            class="mt-4 text-center text-sm font-semibold text-white"
                        >
                            Ir a la herramienta →
                        </div>
                    </div>
                </router-link>

                <router-link
                    to="/app/orders/record"
                    class="text-decoration-none w-full sm:w-80"
                >
                    <div
                        class="button-card h-full bg-brand-red rounded-xl p-5 shadow-sm text-white flex flex-col"
                    >
                        <div class="flex items-start gap-3 mb-3">
                            <span class="text-4xl leading-none">💰</span>
                            <div>
                                <span
                                    class="inline-block text-xs font-semibold bg-white bg-opacity-20 text-white px-2 py-0.5 rounded-full mb-1"
                                >
                                    Sin productos
                                </span>
                                <h5
                                    class="text-white font-semibold leading-tight"
                                >
                                    Venta rápida
                                </h5>
                            </div>
                        </div>

                        <p class="text-sm mb-3 opacity-90">
                            Registra una venta con un monto libre sin necesidad
                            de asociar productos del inventario.
                        </p>

                        <hr class="mb-3 opacity-20" />

                        <ul
                            class="text-sm space-y-2 flex-1 list-none ps-0 opacity-90"
                        >
                            <li class="flex gap-2">
                                <span class="flex-shrink-0">✅</span>
                                <span>
                                    Ingresa el valor de venta manualmente
                                </span>
                            </li>
                            <li class="flex gap-2">
                                <span class="flex-shrink-0">✅</span>
                                <span>
                                    Incrementa automáticamente el dinero en caja
                                </span>
                            </li>
                            <li class="flex gap-2">
                                <span class="flex-shrink-0">✅</span>
                                <span>
                                    Permite asociar cliente y agregar
                                    comentarios
                                </span>
                            </li>
                            <li class="flex gap-2">
                                <span class="flex-shrink-0">⏭</span>
                                <span>No descuenta ni verifica inventario</span>
                            </li>
                        </ul>

                        <div
                            class="mt-4 text-center text-sm font-semibold text-white"
                        >
                            Ir a la herramienta →
                        </div>
                    </div>
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
import WikiLearnMoreLink from "@/components/utils/WikiLearnMoreLink.vue";

export default {
    name: "OrdersHub",
    components: { WikiLearnMoreLink },
    computed: {
        hasMerchantScope() {
            return (
                this.$ability.hasPermission("order:CreateInMerchant") &&
                this.$store.state.auth.tenantWasSelected
            );
        }
    }
};
</script>
